/**
 * The one CSS colour check for an AUTHORED colour: a value a CMS field hands
 * to a stylesheet, kept only when it is a colour and nothing more. Shared by
 * Content Ribbon's three background fields and In-Page Legal's text_color /
 * link_color, so the two cannot grow different grammars. "The band" below is a
 * Content Ribbon background, where a refused colour paints nothing; In-Page
 * Legal falls back to its default colour instead. A caller with a
 * narrower rule (In-Page Legal refuses transparent text) applies it on top of
 * this one, never inside it.
 */

/* ⚠ EVERY PATTERN BELOW IS AN ALLOWLIST, AND THAT IS THE WHOLE DEFENCE. An
   authored colour — a Content Ribbon background, an In-Page Legal text or link
   colour — reaches CSS rather than a text node, so naming what it may NOT
   hold is one new CSS function away from being wrong.

   ⚠ AND EVERY ONE THAT ADMITS WHITESPACE SPELLS THE FIVE CHARACTERS CSS COUNTS
   RATHER THAN WRITING `\s`, WHICH MATCHES TWENTY-FIVE: U+2028, U+2003, U+3000,
   U+1680 and U+FEFF are whitespace to JavaScript and ident characters to CSS,
   so a value separated by one reaches the browser as one unreadable token. */

/** A colour: a 3-, 4-, 6- or 8-digit hex, or one of the four functional forms.
 *  ⚠ NO NESTED FUNCTION — no `var()`, no `calc()`, no `color-mix()` — so the
 *  inner character set carries no bracket and the value holds one pair. The
 *  name is captured because each function's channels differ. */
const SAFE_HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const COLOR_FUNCTION = /^(rgba?|hsla?)\(([0-9a-z.,%/+ \t\n\r\f-]*)\)$/i;

/** A number, signed or not, plain or in scientific notation. Composed rather
 *  than written out three times, so the three kinds of figure below cannot
 *  drift apart. */
const FIGURE_NUMBER = "[+-]?(?:\\d+(?:\\.\\d+)?|\\.\\d+)(?:e[+-]?\\d+)?";
const NUMBER = new RegExp(`^${FIGURE_NUMBER}$`, "i");
const PERCENTAGE = new RegExp(`^${FIGURE_NUMBER}%$`, "i");
const ANGLE = new RegExp(`^${FIGURE_NUMBER}(?:deg|grad|rad|turn)$`, "i");

/** What each function takes in each channel, and what any takes for an alpha.
 *  ⚠ THE UNIT IS PART OF THE CHECK AND NOT ONLY THE DIGITS: `rgb(1deg, 2, 3)`
 *  carries a unit that channel does not take, so the browser drops the
 *  substitution and the band paints nothing. Each set is finite.
 *  ⚠ AND RGB'S TABLE IS THE MODERN FORM'S: the legacy one takes one kind
 *  across all three, which is a rule between them rather than in any of them
 *  and is checked where the grammar is known, in `colorFigures`. */
const RGB_CHANNELS = [
  [NUMBER, PERCENTAGE],
  [NUMBER, PERCENTAGE],
  [NUMBER, PERCENTAGE]
];
/** ⚠ AND HSL TAKES TWO SETS, BECAUSE THIS IS WHERE THE TWO SEPARATOR GRAMMARS
 *  DIFFER IN WHAT THEY HOLD RATHER THAN ONLY IN HOW IT IS DIVIDED: legacy
 *  `hsl(H, S, L)` takes a percentage for saturation and lightness and nothing
 *  else, while the modern `hsl(H S L)` takes a number for either as well. So
 *  `hsl(220 74 15)` is a colour the browser draws and `hsl(220, 74, 15)` is
 *  not, and one set for both would have to refuse one of them wrongly. */
const HSL_LEGACY_CHANNELS = [[NUMBER, ANGLE], [PERCENTAGE], [PERCENTAGE]];
const HSL_MODERN_CHANNELS = [
  [NUMBER, ANGLE],
  [NUMBER, PERCENTAGE],
  [NUMBER, PERCENTAGE]
];
const ALPHA = [NUMBER, PERCENTAGE];

/** Every colour CSS names, plus `transparent`. ⚠ AN ALLOWLIST AND NOT A WORD
 *  TEST: `trasparent` resolves to nothing, so one is a colour here and the
 *  other is not. `currentColor` is absent on purpose — it names whatever the
 *  page around the band inherited rather than a colour an author chose. */
const NAMED_COLORS = new Set(
  ("transparent aliceblue antiquewhite aqua aquamarine " +
    "azure beige bisque black blanchedalmond blue blueviolet brown " +
    "burlywood cadetblue chartreuse chocolate coral cornflowerblue " +
    "cornsilk crimson cyan darkblue darkcyan darkgoldenrod darkgray " +
    "darkgreen darkgrey darkkhaki darkmagenta darkolivegreen darkorange " +
    "darkorchid darkred darksalmon darkseagreen darkslateblue " +
    "darkslategray darkslategrey darkturquoise darkviolet deeppink " +
    "deepskyblue dimgray dimgrey dodgerblue firebrick floralwhite " +
    "forestgreen fuchsia gainsboro ghostwhite gold goldenrod gray green " +
    "greenyellow grey honeydew hotpink indianred indigo ivory khaki " +
    "lavender lavenderblush lawngreen lemonchiffon lightblue lightcoral " +
    "lightcyan lightgoldenrodyellow lightgray lightgreen lightgrey " +
    "lightpink lightsalmon lightseagreen lightskyblue lightslategray " +
    "lightslategrey lightsteelblue lightyellow lime limegreen linen " +
    "magenta maroon mediumaquamarine mediumblue mediumorchid " +
    "mediumpurple mediumseagreen mediumslateblue mediumspringgreen " +
    "mediumturquoise mediumvioletred midnightblue mintcream mistyrose " +
    "moccasin navajowhite navy oldlace olive olivedrab orange orangered " +
    "orchid palegoldenrod palegreen paleturquoise palevioletred " +
    "papayawhip peachpuff peru pink plum powderblue purple " +
    "rebeccapurple red rosybrown royalblue saddlebrown salmon " +
    "sandybrown seagreen seashell sienna silver skyblue slateblue " +
    "slategray slategrey snow springgreen steelblue tan teal thistle " +
    "tomato turquoise violet wheat white whitesmoke yellow yellowgreen").split(" ")
);

/** What a colour name may be written from, asked of the value AS AUTHORED.
 *  ⚠ LOWERCASING IS NOT AN ASCII OPERATION: U+212A, the kelvin sign, folds to
 *  `k`, so a name carrying one passes the list and reaches the browser as an
 *  ident CSS has no colour for. A sweep of the whole of Unicode found that
 *  point and no other, against 31 of the names. */
const COLOR_NAME = /^[a-z]+$/i;

/** How many figures each of the four functional colours is built from: three
 *  channels, or three and an alpha. ⚠ COUNTED FOR THE REASON THE FIGURES
 *  THEMSELVES ARE READ: `rgb(1)` is allowed figures inside an allowed function
 *  and is not a colour, so the browser drops the substitution and the band
 *  paints nothing. */
const CHANNELS_A_COLOR_NEEDS = 3;
const CHANNELS_AND_AN_ALPHA = 4;

/** The field as a trimmed string, or "" for anything that is not a string. */
function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/** CSS's own whitespace, and no more of it — see the head of this file. */
const CSS_WHITESPACE = /[ \t\n\r\f]+/;

/** A run of figures divided by CSS whitespace, which divides nothing at either
 *  end and nothing twice over: `rgb( 0  0  0 )` is a colour the browser draws. */
function words(written: string): string[] {
  return written.split(CSS_WHITESPACE).filter(figure => figure !== "");
}

/** The figures of a colour written the legacy way: a comma between each pair
 *  and a slash nowhere, with something on both sides of every comma. `null`
 *  where the interior is not written that way at all. */
function commaSeparated(interior: string): string[] | null {
  if (interior.includes("/")) {
    return null;
  }

  const written = interior.split(",").map(figure => figure.trim());
  const spelled = written.every(
    figure => figure !== "" && !CSS_WHITESPACE.test(figure)
  );

  return spelled ? written : null;
}

/** And the modern way: three channels divided by whitespace, then at most one
 *  slash, which introduces the alpha and divides nothing else. */
function spaceSeparated(interior: string): string[] | null {
  if (interior.includes(",")) {
    return null;
  }

  const sides = interior.split("/");

  if (sides.length > 2) {
    return null;
  }

  const channels = words(sides[0]);

  if (channels.length !== CHANNELS_A_COLOR_NEEDS) {
    return null;
  }

  if (sides.length === 1) {
    return channels;
  }

  const alpha = words(sides[1]);

  return alpha.length === 1 ? channels.concat(alpha) : null;
}

/** Whether a functional colour's interior is a colour.
 *  ⚠ WHICH SEPARATOR GRAMMAR WAS USED, AND NOT ONLY HOW MANY FIGURES THERE
 *  ARE. CSS writes a colour two mutually exclusive ways — commas and no slash,
 *  or whitespace with one slash before the alpha — and a value mixing them is
 *  neither: `rgb(0 0 0 0.5)` and `rgb(1,2,3/0.5)` are sound figures in the
 *  right number that the browser cannot read, so the property falls back to its
 *  initial value and the band paints NOTHING. A count cannot see the
 *  difference, which is why the two forms are read apart. */
function colorFigures(name: string, interior: string): boolean {
  const legacy = interior.includes(",");
  const written = legacy ? commaSeparated(interior) : spaceSeparated(interior);

  if (written === null) {
    return false;
  }

  if (
    written.length !== CHANNELS_A_COLOR_NEEDS &&
    written.length !== CHANNELS_AND_AN_ALPHA
  ) {
    return false;
  }

  const hsl = name.toLowerCase().startsWith("hsl");
  const channels = hsl ?
    (legacy ? HSL_LEGACY_CHANNELS : HSL_MODERN_CHANNELS) :
    RGB_CHANNELS;

  /* ⚠ AND THE LEGACY rgb() TAKES ONE KIND ACROSS ALL THREE CHANNELS, which is
     a constraint BETWEEN the channels and so no table of theirs can hold it:
     CSS Color 4 writes the comma form as three numbers or three percentages
     and the modern form as either in any channel, so `rgb(100% 0 0)` is a
     colour the browser draws and `rgb(100%, 0, 0)` is one it drops on the
     floor with the band left unpainted. The alpha is free of it in both. */
  if (!hsl && legacy) {
    const kinds = written.slice(0, CHANNELS_A_COLOR_NEEDS);
    const wholly = (kind: RegExp) => kinds.every(figure => kind.test(figure));

    if (!wholly(NUMBER) && !wholly(PERCENTAGE)) {
      return false;
    }
  }

  return written.every((figure, at) => {
    /* `none` stands for a channel left out, and only the modern form takes it. */
    if (figure.toLowerCase() === "none") {
      return !legacy;
    }

    const kinds = at < channels.length ? channels[at] : ALPHA;

    return kinds.some(kind => kind.test(figure));
  });
}

/* `transparent`, or a hex / functional colour whose alpha is 0 or below (CSS
   clamps a negative alpha to 0, and a `none` alpha draws as 0). Asked only of
   a value drawableColor kept, so the shape is already a colour's: a 4- or
   8-digit hex ends on its alpha, and a functional colour's fourth figure is
   its alpha in both the comma and the slash grammar. */
const ZERO_ALPHA_HEX = /^#(?:[0-9a-f]{3}0|[0-9a-f]{6}00)$/i;

/**
 * Whether a kept colour would draw nothing. The NARROWER rule the header above
 * describes — applied ON TOP of `drawableColor`, never inside it, because a
 * see-through background is legitimate where a see-through text or border is not.
 *
 * Shared rather than copied: In-Page Legal refuses invisible text and ZipCode
 * refuses an invisible field border, and the alpha grammar here (4- and 8-digit
 * hex, `none`, comma and slash forms) is too easy to get subtly different twice.
 */
export function isInvisibleColor(colour: string): boolean {
  if (colour?.toLowerCase?.() === "transparent" || ZERO_ALPHA_HEX.test(colour)) {
    return true;
  }
  const alpha = /\(([^)]*)\)$/.exec(colour)?.[1]?.split(/[\s,/]+/)?.filter(Boolean)?.[3];
  return alpha !== undefined && (alpha?.toLowerCase?.() === "none" || parseFloat(alpha) <= 0);
}

/** The authored colour as written (trimmed), or "" for anything that is not
 *  one. ⚠ THE ONE CHECK EVERY AUTHORED COLOUR FIELD GOES THROUGH — Content
 *  Ribbon's flat colour and both gradient ends, In-Page Legal's text_color and
 *  link_color — so a value an author may write in one field is one they may
 *  write in another: a validator cannot accept a colour in one field and refuse
 *  it in another. Keeps `transparent` (a background may be see-through). */
export function drawableColor(raw: unknown): string {
  const value = text(raw);
  const named = COLOR_NAME.test(value) && NAMED_COLORS.has(value.toLowerCase());

  if (SAFE_HEX.test(value) || named) {
    return value;
  }

  const functional = COLOR_FUNCTION.exec(value);

  return functional !== null && colorFigures(functional[1], functional[2]) ?
    value :
    "";
}
